import axiosInstance from '../../utils/axiosInstance';
import dayjs from 'dayjs';

export interface BankInfo {
  name: string;
}

export interface CreditCardInfo {
  cardNumber: string;
  cardHolderName: string;
  bank?: BankInfo;
  cardType?: string;
}

export interface MaturityScheduleInfo {
  id: string;
  dueDate: string;
  creditCard?: CreditCardInfo;
}

export interface CustomerInfo {
  fullName: string;
  phoneNumber: string;
  email: string;
}

export interface EmployeeInfo {
  name: string;
}

export interface TransactionInfo {
  id: string;
  transactionType: string;
  amount: number;
  balance: number;
  creditCard?: CreditCardInfo;
}

export interface PaymentDetail {
  id: string;
  status: string;
  amount: number;
  paymentDate: string;
  maturityScheduleId?: string;
  transactionId?: string;
  maturitySchedule?: MaturityScheduleInfo;
  transaction?: TransactionInfo;
  customer?: CustomerInfo;
  employee?: EmployeeInfo;
  createdAt: string;
}

export interface TransactionDetail {
  id: string;
  status: string;
  transactionType: string;
  amount: number;
  balance: number;
  transactionDate: string;
  maturityScheduleId?: string;
  cardNumber?: string;
  employee?: EmployeeInfo;
  creditCard?: CreditCardInfo;
  maturitySchedule?: MaturityScheduleInfo;
  customer?: CustomerInfo;
  createdAt: string;
}

export interface FinancialRecordDetail {
  id: string;
  recordType: string;
  amount: number;
  recordDate: string;
  description: string;
  relatedTransactionId?: string;
  employee?: EmployeeInfo;
  relatedTransaction?: TransactionInfo;
  createdAt: string;
}

export interface MaturityRevenue {
  totalRevenue: number;
  totalExpense: number;
  netProfit: number;
  paymentRevenue: number;
  posRevenue: number;
  posExpense: number;
  relatedExpense: number;
  paymentDetails: PaymentDetail[];
  withdrawalDetails: TransactionDetail[];
  depositDetails: TransactionDetail[];
}

export interface WithdrawalRevenue {
  totalRevenue: number;
  totalExpense: number;
  netProfit: number;
  paymentRevenue: number;
  posRevenue: number;
  posExpense: number;
  relatedExpense: number;
  paymentDetails: PaymentDetail[];
  withdrawalDetails: TransactionDetail[];
  relatedExpenseDetails: FinancialRecordDetail[];
}

export interface FinancialRecords {
  totalRevenue: number;
  totalExpense: number;
  netProfit: number;
  incomeDetails: FinancialRecordDetail[];
  expenseDetails: FinancialRecordDetail[];
}

export interface ProfitData {
  totalRevenue: number;
  totalExpense: number;
  netProfit: number;
  totalDebt?: number;
  maturityRevenue: MaturityRevenue;
  withdrawalRevenue: WithdrawalRevenue;
  financialRecords: FinancialRecords;
}

class ProfitReportService {
  async getProfitReport(
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ): Promise<ProfitData> {
    const response = await axiosInstance.get(`/profit-report`, {
      params: {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      },
    });
    return response.data;
  }

  async getMaturityProfitReport(
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ): Promise<MaturityRevenue> {
    const response = await axiosInstance.get(`/profit-report/maturity`, {
      params: {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      },
    });
    return response.data;
  }

  async getWithdrawalProfitReport(
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ): Promise<WithdrawalRevenue> {
    const response = await axiosInstance.get(
      `/profit-report/withdrawal`,
      {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
        },
      }
    );
    return response.data;
  }

  async getFinancialRecordsReport(
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ): Promise<FinancialRecords> {
    const response = await axiosInstance.get(
      `/profit-report/financial-records`,
      {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
        },
      }
    );
    return response.data;
  }

  async getYearlyProfitSummary(year: number): Promise<{
    months: {
      month: number;
      totalRevenue: number;
      totalExpense: number;
      netProfit: number;
      totalDebt: number;
    }[];
  }> {
    const response = await axiosInstance.get(
      `/profit-report/yearly-summary`,
      {
        params: { year },
      }
    );
    return response.data;
  }
}

export default new ProfitReportService();
